import React, { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { formatBuild as fmtBuild } from '../utils/formatBuild';

/**
 * App-admin view of the DEPLOY VERSION LOG for a single app.
 *
 * Reads the durable version report (persisted to S3 in prod / a local file in
 * dev) via GET /api/apps/:appName/version-report — a manager-gated endpoint, so
 * the app's owner, a co-admin, or a Lyric admin may open it from the launch-page
 * tile without needing full site-admin access. Falls back to the app's live
 * in-memory deploy history when no stored report exists yet.
 *
 * Supports exporting the report as a JSON file.
 *
 * Props:
 *   appName     registry key of the app
 *   displayName friendly name (header)
 *   apiUrl      API base URL
 *   authFetch   (url, opts) => fetch, injects the Okta bearer token
 *   onClose     close the modal
 */
const ACTION_COLOR = { initial: '#80cbc4', bump: '#81c784', set: '#ffb74d', manual: '#ff8a65' };
const SEMVER_RE = /^\d{1,6}(\.\d{1,6}){0,2}$/;

const VersionLogModal = ({ appName, displayName, apiUrl, authFetch, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);
  const [source, setSource] = useState(null);
  const [storeLocation, setStoreLocation] = useState(null);
  // Manual version-correction form (managers only).
  const [editing, setEditing] = useState(false);
  const [newVersion, setNewVersion] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [savedFlash, setSavedFlash] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch(`${apiUrl}/api/apps/${encodeURIComponent(appName)}/version-report`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setReport(data.report || null);
      setSource(data.source || null);
      setStoreLocation(data.storeLocation || null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authFetch, apiUrl, appName]);

  useEffect(() => { load(); }, [load]);

  // Close on Escape (matches the app-wide modal behavior).
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  const exportJson = () => {
    if (!report) return;
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${appName}-version-report.json`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  // Open the manual-correction form, prefilling the current version.
  const openEdit = () => {
    setNewVersion(report?.currentVersion || '');
    setReason('');
    setSaveError(null);
    setEditing(true);
  };

  const versionClean = String(newVersion || '').trim().replace(/^v/i, '');
  const versionValid = SEMVER_RE.test(versionClean);
  const reasonClean = reason.trim();
  const canSubmit = versionValid && reasonClean.length > 0 && !saving;

  // POST the manual version correction, then reload the log so the new 'manual'
  // row (and updated current version) shows immediately.
  const submitVersion = async () => {
    if (!canSubmit) return;
    setSaving(true);
    setSaveError(null);
    try {
      const res = await authFetch(`${apiUrl}/api/apps/${encodeURIComponent(appName)}/version`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ version: versionClean, reason: reasonClean }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setEditing(false);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2500);
      await load();
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const history = Array.isArray(report?.history) ? [...report.history].reverse() : [];
  const fmt = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
  };

  const th = { textAlign: 'left', padding: '6px 10px', color: '#aaa', borderBottom: '1px solid #2a2a3a', fontWeight: 600, fontSize: 12, position: 'sticky', top: 0, background: '#14141f' };
  const td = { padding: '6px 10px', borderBottom: '1px solid #1a1a26', fontSize: 12, color: '#cfd8dc', verticalAlign: 'top' };

  return ReactDOM.createPortal(
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 10000, display: 'flex',
        alignItems: 'center', justifyContent: 'center', padding: 16,
        backgroundColor: 'rgba(0,0,0,0.6)',
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Deploy version log"
        style={{
          width: '100%', maxWidth: 720, maxHeight: '90vh', display: 'flex', flexDirection: 'column',
          backgroundColor: '#14141f', border: '1px solid #333', borderRadius: 12,
          boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 20px', borderBottom: '1px solid #2a2a3a' }}>
          <span aria-hidden="true" style={{ fontSize: 20 }}>🕘</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3 style={{ margin: 0, fontSize: 16, color: '#fff' }}>Deploy version log</h3>
            <div style={{ fontSize: 12, color: '#8a97a8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayName || appName}
              {report?.currentVersion ? <> · current <code style={{ color: '#80cbc4' }}>v{report.currentVersion}</code></> : null}
              {fmtBuild(report?.currentBuild) ? <> · build <code style={{ color: '#80cbc4' }}>{fmtBuild(report.currentBuild)}</code></> : null}
            </div>
          </div>
          <button
            onClick={editing ? () => setEditing(false) : openEdit}
            disabled={!report}
            style={{ padding: '6px 12px', fontSize: 12, fontWeight: 600, background: editing ? '#3a2416' : '#2a1f12', color: '#ffb74d', border: '1px solid #7a5216', borderRadius: 6, cursor: report ? 'pointer' : 'default' }}
            title="Manually correct this app's version (e.g. if auto-versioning recorded the wrong number). A reason is required and the change is logged."
          >
            {editing ? '✕ Cancel edit' : '✏️ Correct version'}
          </button>
          <button
            onClick={exportJson}
            disabled={!report}
            style={{ padding: '6px 12px', fontSize: 12, fontWeight: 600, background: '#152238', color: '#7cc4ff', border: '1px solid #2f4a6a', borderRadius: 6, cursor: report ? 'pointer' : 'default' }}
            title="Export this version report as a JSON file"
          >
            ⬇ Export JSON
          </button>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', fontSize: 22, lineHeight: 1, padding: 0 }}
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '14px 20px', overflowY: 'auto' }}>
          {savedFlash && (
            <div style={{ color: '#66bb6a', fontSize: 13, marginBottom: 12 }}>✓ Version updated and logged.</div>
          )}

          {/* Manual version-correction form (managers). Applies live, no redeploy. */}
          {editing && (
            <div style={{ marginBottom: 16, padding: 14, borderRadius: 8, background: '#1c150c', border: '1px solid #7a5216' }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#ffb74d', marginBottom: 4 }}>✏️ Correct version</div>
              <div style={{ fontSize: 12, color: '#9a8b78', marginBottom: 12, lineHeight: 1.5 }}>
                Sets the recorded version <b>immediately</b> (no redeploy) and adds a logged <code style={{ color: '#ff8a65' }}>manual</code> entry.
                The running app’s injected version only refreshes on its next deploy.
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-start' }}>
                <label style={{ fontSize: 12, color: '#cfd8dc' }}>
                  New version
                  <input
                    type="text"
                    value={newVersion}
                    onChange={(e) => setNewVersion(e.target.value)}
                    placeholder="2.1.0"
                    disabled={saving}
                    style={{
                      display: 'block', marginTop: 4, width: 120, padding: '7px 10px', borderRadius: 6, fontSize: 13,
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, monospace',
                      background: '#222', color: '#e0e0e0',
                      border: `1px solid ${newVersion && !versionValid ? '#ef5350' : '#444'}`,
                    }}
                  />
                </label>
                <label style={{ fontSize: 12, color: '#cfd8dc', flex: 1, minWidth: 220 }}>
                  Reason for change <span style={{ color: '#ef9a9a' }}>*</span>
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Why is this manual correction needed? (e.g. auto-redeploy bumped to 3.0.0 by mistake)"
                    disabled={saving}
                    rows={2}
                    maxLength={500}
                    style={{
                      display: 'block', marginTop: 4, width: '100%', padding: '7px 10px', borderRadius: 6, fontSize: 13,
                      background: '#222', color: '#e0e0e0', border: '1px solid #444', resize: 'vertical', boxSizing: 'border-box',
                    }}
                  />
                </label>
              </div>
              {newVersion && !versionValid && (
                <p style={{ color: '#ef9a9a', fontSize: 11, margin: '8px 0 0' }}>Enter a valid MAJOR.MINOR.PATCH version (e.g. 2.1.0).</p>
              )}
              {saveError && <p style={{ color: '#ef5350', fontSize: 12, margin: '8px 0 0' }}>⚠️ {saveError}</p>}
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button
                  onClick={submitVersion}
                  disabled={!canSubmit}
                  style={{
                    padding: '8px 16px', borderRadius: 6, fontSize: 13, fontWeight: 600, border: 'none', color: '#111',
                    cursor: canSubmit ? 'pointer' : 'not-allowed', background: canSubmit ? '#ffb74d' : '#555',
                  }}
                >{saving ? 'Saving…' : 'Save version'}</button>
                <button
                  onClick={() => setEditing(false)}
                  disabled={saving}
                  style={{ padding: '8px 16px', borderRadius: 6, fontSize: 13, fontWeight: 600, background: 'none', color: '#aaa', border: '1px solid #444', cursor: 'pointer' }}
                >Cancel</button>
              </div>
            </div>
          )}

          {loading && <div style={{ color: '#aaa', fontSize: 13 }}>Loading…</div>}
          {error && <div style={{ color: '#ef9a9a', fontSize: 13 }}>⚠️ {error}</div>}
          {!loading && !error && (
            <>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, fontSize: 12, color: '#8a97a8', marginBottom: 12 }}>
                <span>{history.length} deploy{history.length === 1 ? '' : 's'} recorded</span>
                {source && <span>source: <code style={{ color: '#9fb3c8' }}>{source === 'store' ? 'stored report' : 'live history'}</code></span>}
                {storeLocation && <span title={storeLocation} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 360 }}>📦 {storeLocation}</span>}
              </div>
              {history.length === 0 ? (
                <div style={{ color: '#666', fontSize: 13, fontStyle: 'italic' }}>
                  No deploy history recorded yet. It's captured on the next deploy.
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={th}>Version</th>
                      <th style={th}>Build</th>
                      <th style={th}>Commit</th>
                      <th style={th}>Action</th>
                      <th style={th}>When</th>
                      <th style={th}>By</th>
                      <th style={th}>Branch</th>
                      <th style={th}>Trigger</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((h, i) => (
                      <tr key={`${h.version}-${h.at}-${i}`}>
                        <td style={{ ...td, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, monospace', color: '#e0e0e0', fontWeight: 700 }}>v{h.version}</td>
                        <td style={{ ...td, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, monospace', color: '#9fb3c8' }}>{fmtBuild(h.build) || '—'}</td>
                        <td style={{ ...td, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, monospace', color: '#9fb3c8' }}>{h.commitSha ? h.commitSha.slice(0, 7) : '—'}</td>
                        <td style={{ ...td, color: ACTION_COLOR[h.action] || '#cfd8dc', fontWeight: 600 }}>{h.action}{h.bumpType ? ` · ${h.bumpType}` : ''}</td>
                        <td style={td}>{fmt(h.at)}</td>
                        <td style={td}>{h.by || '—'}</td>
                        <td style={td}>{h.branch || '—'}</td>
                        <td style={td}>{h.trigger || '—'}{h.reason ? ` · ${h.reason}` : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default VersionLogModal;
